import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  TextField
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';

import { Invoice } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { getCommentsAndTagsSummary } from 'utils/helpers/invoiceSectionSummaryHelpers';

import {
  verificationFormFieldsWrapper,
  verificationFormItemNoHeight
} from 'styles/pages/InvoiceVerificationStyle';

import { InvoiceVerificationCard } from './InvoiceVerificationCard';

interface CommentsAndTagsCardProps {
  currentInvoice: Invoice;
  onExpanded?: () => void;
}

export const CommentsAndTagsCard = ({
  currentInvoice,
  onExpanded
}: CommentsAndTagsCardProps) => {
  const { translate } = useTranslations();
  const { control } = useFormContext();
  const { getAllTagsByCompanyId } = useAdditionalFieldsController();

  const documentDescription = useWatch({
    name: InvoiceVerificationFieldName.DocumentDescription
  });
  const watchedTags =
    useWatch({ name: InvoiceVerificationFieldName.Tags }) || [];

  const summary = useMemo(
    () =>
      getCommentsAndTagsSummary(
        { documentDescription, tags: watchedTags },
        translate
      ),
    [documentDescription, watchedTags, translate]
  );

  const [tags, setTags] = useState<string[]>(currentInvoice.tags || []);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const filter = createFilterOptions<string>();

  const fetchAvailableTags = useCallback(async () => {
    const tagsResponse = await getAllTagsByCompanyId(
      Number(currentInvoice.companyId)
    );
    setAvailableTags(Array.from(new Set([...tags, ...tagsResponse])));
  }, [getAllTagsByCompanyId, currentInvoice.companyId, tags]);

  useEffect(() => {
    fetchAvailableTags();
  }, [fetchAvailableTags]);

  useEffect(() => {
    setAvailableTags((previousTags) =>
      Array.from(new Set([...tags, ...previousTags]))
    );
  }, [tags]);

  return (
    <InvoiceVerificationCard
      title={translate('labels.commentAndTags')}
      summary={summary}
      summaryEqualWidths
      onExpanded={onExpanded}
      defaultExpanded={false}
    >
      <Box
        sx={{
          ...verificationFormFieldsWrapper,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2
        }}
      >
        <Controller
          name={InvoiceVerificationFieldName.DocumentDescription}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              multiline
              minRows={2}
              maxRows={2}
              label={translate('labels.comment')}
              name={InvoiceVerificationFieldName.DocumentDescription}
              placeholder={translate('labels.comment')}
              sx={verificationFormItemNoHeight}
            />
          )}
        />
        <Controller
          name={InvoiceVerificationFieldName.Tags}
          control={control}
          render={({ field }) => (
            <Autocomplete
              {...field}
              fullWidth
              sx={{ flex: 1 }}
              freeSolo
              multiple
              disableCloseOnSelect
              options={availableTags}
              value={tags}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                  const isExisting = options.some(
                    (option) =>
                      params.inputValue.toLowerCase() === option.toLowerCase()
                  );
                  if (!isExisting) {
                    filtered.push(`Add "${params.inputValue}"`);
                  }
                }

                return filtered;
              }}
              getOptionLabel={(option) => {
                if (option.startsWith('Add "')) {
                  return option;
                }
                return option;
              }}
              onChange={(_event, newValue) => {
                const lastValue = newValue[newValue.length - 1];
                if (lastValue && lastValue.startsWith('Add "')) {
                  const newTag = lastValue.slice(5, -1);
                  const updatedTags = [...tags, newTag];
                  setTags(updatedTags);
                  field.onChange(updatedTags);
                } else {
                  const uniqueTags = Array.from(new Set(newValue));
                  setTags(uniqueTags);
                  field.onChange(uniqueTags);
                }
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li key={key} {...rest}>
                    {option.startsWith('Add "') ? <em>{option}</em> : option}
                  </li>
                );
              }}
              renderValue={(value: string[], getItemProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    sx={{ cursor: 'initial' }}
                    variant="outlined"
                    label={option}
                    {...getItemProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('labels.tags')}
                  placeholder={translate('labels.typeAndPressEnterToAddTag')}
                />
              )}
            />
          )}
        />
      </Box>
    </InvoiceVerificationCard>
  );
};
